import { SubstepType } from "~/Configurators/Linear/dtos";
import { isNonNull, toMapFn } from "~/utils/reducers";
import type { SummaryData, SummaryDataProvider } from "~/Configurators/Shaft/SummaryDataProvider";
import { removeMultipleTags } from "~/utils/domUtils";
import { CsticNames } from "~/Configurators/Carriage/components/ConfigureCarriageSubstep/constants";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import { getMainKmatCstics } from "~/Configurators/common/v2/helper";

export function getStep1SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const substeps = [SubstepType.Coating, SubstepType.Preload, SubstepType.Accuracy, SubstepType.Lubrication];
  return prepareSubStepsInfo(summaryProvider, substeps);
}

export function getStep2SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const substeps = [SubstepType.CarriageType, SubstepType.LocatingFace];

  const summaryData = prepareSubStepsInfo(summaryProvider, substeps) ?? [];

  const configureCarriageSubstep = summaryProvider.substeps[SubstepType.ConfigureCarriage];
  if (configureCarriageSubstep) {
    const t = summaryProvider.t;

    [CsticNames.KITLeft, CsticNames.KITCenter, CsticNames.KITRight]
      .map((name) => getMainKmatCstics(configureCarriageSubstep).input[name])
      .filter((def) => def?.visible)
      .map((def) => {
        const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;
        return cstics[def.name];
      })
      .filter((cstic) => cstic?.value)
      .forEach((cstic) => {
        const csticValue = cstic.values.find((c) => c.value === cstic.value);
        summaryData.push({
          name: t(cstic.label),
          isStandard: false,
          value: csticValue?.label ?? "",
        });
      });

    [CsticNames.LubricationConnectorOrientation]
      .map((name) => getMainKmatCstics(configureCarriageSubstep).input[name])
      .filter((def) => def?.visible)
      .map((def) => {
        const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;
        return cstics[def.name];
      })
      .filter((cstic) => cstic?.value)
      .forEach((cstic) => {
        const csticValue = cstic.values.find((c) => c.value === cstic.value);
        summaryData.push({
          name: t(cstic.label),
          isStandard: false,
          value: t(csticValue?.label ?? ""),
        });
      });

    [CsticNames.LubricationConnectorPosition, CsticNames.LubricationConnector]
      .map((name) => getMainKmatCstics(configureCarriageSubstep).input[name])
      .filter((def) => def?.visible)
      .map((def) => {
        const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;
        return cstics[def.name];
      })
      .filter((cstic) => cstic?.value)
      .forEach((cstic) => {
        const csticValue = cstic.values.find((c) => c.value === cstic.value);
        summaryData.push({
          name: t(cstic.label),
          isStandard: false,
          value: csticValue?.label ?? "",
        });
      });
  }

  summaryData.forEach((data) => {
    data.value = removeMultipleTags(data.value, "p", "span")!;
    data.name = removeMultipleTags(data.name, "p", "span")!;
  });

  return summaryData;
}

export function prepareSubStepsInfo(summaryProvider: SummaryDataProvider, substeps: SubstepType[]) {
  const substepData = substeps
    .map((substepType) => [substepType, summaryProvider.prepareTileSubstepSummary(substepType)] as const)
    .filter(([, summary]) => isNonNull(summary)) as [SubstepType, SummaryData][];

  if (substepData.length === 0) {
    return null;
  }

  substepData.forEach((data) => {
    data[1].value = removeMultipleTags(data[1].value, "p", "span")!;
    data[1].name = removeMultipleTags(data[1].name, "p", "span")!;
  });

  const data: Record<string, SummaryData> = toMapFn(
    substepData,
    ([type]) => type,
    ([, summary]) => summary,
  );

  return substeps.map((stepName) => data[stepName]).filter(Boolean) as SummaryData[];
}
